import { TYPE_SEGMENTS } from "./productUrl";

// Drafts are namespaced per user: logout() only clears a fixed set of keys, so
// an un-namespaced key would hand one admin's drafts to the next person who
// logs in on the same browser.
const DRAFTS_KEY_PREFIX = "fabricGroupDrafts:v1:";

const draftsKey = () =>
  DRAFTS_KEY_PREFIX + (localStorage.getItem("userId") || "anon");

export const isGroupAdmin = () =>
  localStorage.getItem("userPrivileges") === "admin";

// A fabric, a knit and a stock can all share the same numeric id, so anything
// that identifies an item has to carry its type too.
export const itemKey = (item) => `${item.itemType}:${item.id}`;

export const newLocalId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `g_${crypto.randomUUID()}`;
    }
  } catch (err) {
    // fall through to the timestamp form below
  }
  return `g_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

export const readDrafts = () => {
  try {
    const raw = localStorage.getItem(draftsKey());
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error("Could not read fabric group drafts:", err);
    return [];
  }
};

export const writeDrafts = (drafts) => {
  try {
    localStorage.setItem(draftsKey(), JSON.stringify(drafts));
  } catch (err) {
    console.error("Could not save fabric group drafts:", err);
  }
  return drafts;
};

// Same rules the bookmark action uses, so an item added from a card and the
// same item added from its detail page end up with the same type.
export const resolveItemType = (explicitType) => {
  if (explicitType) return explicitType;

  const viewMode = localStorage.getItem("viewMode");
  const categoryName = localStorage.getItem("category");
  const isKnitsMode = localStorage.getItem("isKnitsMode");

  if (viewMode === "stocks" || window.location.pathname.includes("/stocks")) {
    return "stock";
  }
  if (
    categoryName === "knits" ||
    isKnitsMode === "true" ||
    window.location.pathname.includes("/knits")
  ) {
    return "knit";
  }
  return "fabric";
};

// Keep enough of the product to render the draft list without a network call.
export const buildItemSnapshot = (product, itemType) => {
  const a = product?.attributes || {};
  const image =
    a.primary_image?.data?.attributes?.formats?.medium?.url ??
    a.primary_image?.data?.attributes?.url ??
    a.image?.data?.[0]?.attributes?.formats?.medium?.url ??
    a.image?.data?.[0]?.attributes?.url ??
    null;

  return {
    id: product.id,
    itemType,
    addedAt: Date.now(),
    snapshot: {
      fabric_name: a.fabric_name ?? "",
      fabric_id: a.fabric_id ?? null,
      // Stocks expose `price`; everything else `fabric_price`.
      fabric_price: a.fabric_price ?? a.price ?? null,
      gsm: a.gsm ?? null,
      width_label:
        a.width?.data?.attributes?.label ??
        a.width?.data?.[0]?.attributes?.label ??
        null,
      image,
    },
  };
};

export const createDraft = (name, description = "") => {
  const draft = {
    localId: newLocalId(),
    name: (name || "").trim() || "Untitled group",
    description,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    items: [],
  };
  writeDrafts([draft, ...readDrafts()]);
  return draft;
};

export const deleteDraft = (localId) =>
  writeDrafts(readDrafts().filter((d) => d.localId !== localId));

export const draftHasItem = (draft, id, itemType) =>
  (draft?.items || []).some(
    (item) => item.id === id && item.itemType === itemType
  );

export const addItemToDraft = (localId, product, itemType) => {
  const type = resolveItemType(itemType);
  return writeDrafts(
    readDrafts().map((draft) => {
      if (draft.localId !== localId) return draft;
      if (draftHasItem(draft, product.id, type)) return draft;
      return {
        ...draft,
        updatedAt: Date.now(),
        items: [...draft.items, buildItemSnapshot(product, type)],
      };
    })
  );
};

export const removeItemFromDraft = (localId, id, itemType) =>
  writeDrafts(
    readDrafts().map((draft) =>
      draft.localId === localId
        ? {
            ...draft,
            updatedAt: Date.now(),
            items: draft.items.filter(
              (item) => !(item.id === id && item.itemType === itemType)
            ),
          }
        : draft
    )
  );

// Strapi wants the three relations split out by collection.
export const buildGroupConnectPayload = (draft) => {
  const byType = { fabric: [], knit: [], stock: [] };
  (draft?.items || []).forEach((item) => {
    if (byType[item.itemType]) byType[item.itemType].push(item.id);
  });
  return {
    fabrics: { connect: byType.fabric },
    knits: { connect: byType.knit },
    stocks: { connect: byType.stock },
  };
};

export const SHARE_PATH = "/shared-group";

export const buildShareUrl = (groupId) =>
  `${window.location.origin}${process.env.PUBLIC_URL}${SHARE_PATH}/${groupId}`;

export const typeSegmentFor = (itemType) =>
  TYPE_SEGMENTS[itemType] || TYPE_SEGMENTS.fabric;
